// The expected-invoice calculation. Shared verbatim between the UI's
// Recalculate button and the nightly bill-run job — ONE implementation, because
// two copies of a money calculation drift and then two screens disagree about
// what a client owes.
//
// Hence the `db` parameter instead of a module-level client import. This file
// used to import the browser client from foundation/.../supabaseClient, which
// resolves its key from import.meta.env and falls back to the ANON key under
// Node. On the server that would not throw — it would read zero contract rows
// through RLS and bill every site at $0.00. The parameter is required, with no
// default, so no caller can reach that state by omission.
import type { SupabaseClient } from '@supabase/supabase-js';
import { keepReducedBucket } from '../lib/reducedBucket';
import {
  RETAIL_ENERGY_NAMES, supplyChargeName, meteringChargeName, perMeterChargeName, chargeRateUnit, chargeHolderOf,
  quantityUnitForPriceType,
} from '../utils/chargeNames';
import type { BvBill } from '../types/billTypes';
import type {
  ShadowBillResult, ShadowBillLine, ShadowBillSection,
} from '../types/billTypes';
// Job 35: the same period-selection the network card uses, so the figures the
// card shows for a date are the figures this prices that date with.
import { currentNetworkCode, ratePeriodForDate } from '../utils/networkContracts';
import { mlfForPeriod, dlfForPeriod } from '../utils/lossFactorFy';
import type { BvNetworkContract, BvNetworkRate } from '../types/billValidationTypes';
import { costOf } from '../lib/costOf';
import { canonicalName, loadNameAliases } from '../utils/nameAliases';

type MeteringRateRow = {
  period_start?: string;
  period_end?: string;
  metering_annual?: number; // $/year — prorated to billing period
  /** The unit `metering_annual` was actually quoted in. The field name reflects the
   *  long-standing assumption that metering is annual; now that the unit is editable
   *  the assumption has to be checked rather than trusted. */
  metering_unit?: string;
  supply_per_day?: number;  // $/day meter rental/service
  /** The contract's other per-meter charges, quoted the same way as metering
   *  (bv_contract_rate.supplementary_metering / supplementary_services and
   *  their units). Billed by retailers as e.g. "Supplementary Metering Charge"
   *  and "Value Added Service Charge". */
  supp_metering?: number;
  supp_metering_unit?: string;
  supp_services?: number;
  supp_services_unit?: string;
};

type SiteContracts = {
  retail: RetailRateRow | null;
  retailRateFallback: boolean;
  metering: MeteringRateRow | null;
  distribution: DistributionRow | null;
  environmental: EnvRow[];
  mlf: number;
  dlf: number;
  mlf_configured: boolean;
  dlf_configured: boolean;
  /** Financial year the pinned factor was resolved for, when recorded. */
  mlf_fy: string | null;
  dlf_fy: string | null;
};

type RetailRateRow = {
  period_start?: string;
  period_end?: string;
  peak_rate?: number;
  shoulder_rate?: number;
  off_peak_rate?: number;
  super_peak_rate?: number;
  supply_charge?: number;
  supply_charge_unit?: string;
  feed_in_rate?: number;
  // Banded buy-back. A retailer that prices export by time of use populates
  // these instead of feed_in_anytime — they are not a refinement of it, they
  // are the alternative, and on AGL only these are set.
  feed_in_peak_rate?: number;
  feed_in_shoulder_rate?: number;
  feed_in_off_peak_rate?: number;
};

type DbRateRow = {
  period_start?: string;
  period_end?: string;
  peak?: number;
  shoulder?: number;
  off_peak?: number;
  super_peak?: number;
  supply?: number;
  supply_unit?: string;
  metering?: number;
  metering_unit?: string;
  supplementary_metering?: number;
  supp_metering_unit?: string;
  supplementary_services?: number;
  supp_services_unit?: string;
  feed_in_anytime?: number;
  feed_in_peak?: number;
  feed_in_shoulder?: number;
  feed_in_off_peak?: number;
};

// The hand-kept network charges the distribution section falls back to when the
// pipeline has produced no priced network usage.
//
// Job 35: these used to be a `contract_type='distribution'` jsonb payload with
// one undated set of figures. They now come off the network contract covering
// the bill (`contract_type='network'`) and its rate period — same fields, same
// arithmetic downstream, but selected by date instead of by "the only row
// there".
type DistributionRow = {
  tariff_code?: string;
  peak_charge?: number;
  shoulder_charge?: number;
  off_peak_charge?: number;
  // A flat network tariff quotes one rate for all consumption. Without this a
  // site on one charged nothing at all for network usage.
  anytime_charge?: number;
  has_demand_contract?: boolean;
  supply_charge_per_day?: number;
  peak_demand_rate?: number;
  anytime_demand_rate?: number;
};

type EnvRow = {
  certificate_type: string;
  rate?: number;
  rate_unit?: string;
};

/** A `contract_type='network'` row as fetched, with its rate periods joined. */
type NetworkContractRow = {
  id: string;
  supplier_key?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  data?: Record<string, unknown> | null;
  bv_contract_rate?: DbRateRow[];
};

/** Days in a `$/period` charge, for converting a stored unit to $/day. */
const DAYS_PER = { day: 1, month: 365 / 12, year: 365 } as const;

/**
 * A network standing charge as $/day, whatever unit it is stored in.
 *
 * The old distribution payload had a column literally named
 * `supply_charge_per_day`, so a monthly or annual figure typed into it was
 * billed as if it were daily — job 37's mistake with a different label. The
 * network rate period stores the unit beside the amount, so it is converted
 * rather than assumed.
 */
export function supplyPerDay(amount: number | undefined, unit: string | undefined): number | undefined {
  if (amount == null) return undefined;
  const period = unit?.endsWith('_year') ? 'year' : unit?.endsWith('_month') ? 'month' : 'day';
  return amount / DAYS_PER[period];
}

/**
 * The hand-entered network charges that price `billFrom`, or null.
 *
 * Two selections, in order: which tariff code the site was on for that date,
 * then which of that code's rate periods covers it. Both are the pure helpers
 * the UI uses, so the card and the calculation cannot disagree about which
 * period applies — the disagreement job 35 was reported for.
 */
function networkFallbackRow(
  rows: NetworkContractRow[] | null,
  billFrom: string,
): DistributionRow | null {
  if (!rows || rows.length === 0) return null;

  const contracts: BvNetworkContract[] = rows.map((r) => ({
    id: r.id,
    org_id: '',
    site_id: '',
    tariff_code: (r.supplier_key ?? (r.data?.tariff_code as string) ?? '').trim(),
    valid_from: r.valid_from ?? undefined,
    valid_to: r.valid_to ?? undefined,
    rate_type: 'stepped',
    has_demand_contract: (r.data?.has_demand_contract as boolean) ?? false,
    is_current: true,
    created_at: '',
    updated_at: '',
  }));

  const code = currentNetworkCode(contracts, billFrom);
  if (!code) return null;

  const row = rows.find(
    (r) => ((r.supplier_key ?? (r.data?.tariff_code as string) ?? '').trim().toLowerCase())
      === code.toLowerCase(),
  );
  if (!row) return null;

  const periods: BvNetworkRate[] = (row.bv_contract_rate ?? []).map((r) => ({
    id: (r as unknown as { id: string }).id,
    period_start: r.period_start,
    period_end: r.period_end,
  }));
  const chosen = ratePeriodForDate(periods, billFrom);
  if (!chosen) return null;

  // Read the money off the raw row rather than the mapped one — the mapping
  // above carries only what the period selection needs.
  const raw = (row.bv_contract_rate ?? []).find(
    (r) => (r as unknown as { id: string }).id === chosen.id,
  ) as (DbRateRow & {
    anytime?: number;
    peak_demand_rate?: number;
    anytime_demand_rate?: number;
  }) | undefined;
  if (!raw) return null;

  return {
    tariff_code: code,
    peak_charge: raw.peak ?? undefined,
    shoulder_charge: raw.shoulder ?? undefined,
    off_peak_charge: raw.off_peak ?? undefined,
    anytime_charge: raw.anytime ?? undefined,
    has_demand_contract: (row.data?.has_demand_contract as boolean) ?? false,
    supply_charge_per_day: supplyPerDay(raw.supply ?? undefined, raw.supply_unit ?? undefined),
    peak_demand_rate: raw.peak_demand_rate ?? undefined,
    anytime_demand_rate: raw.anytime_demand_rate ?? undefined,
  };
}

async function fetchContracts(db: SupabaseClient, siteId: string, billFrom: string, billTo: string): Promise<SiteContracts> {
  const [retailRes, distRes, envRes, siteRes] = await Promise.all([
    db
      .from('bv_contract')
      .select('*, bv_contract_rate(*)')
      .eq('site_id', siteId)
      .eq('contract_type', 'retail')
      .eq('is_current', true)
      .limit(1)
      .maybeSingle(),
    // Job 35: network contracts, with their rate periods. Every contract for the
    // site is fetched rather than one filtered in SQL — the "which code was this
    // site on" decision is `currentNetworkCode`, which prefers a dated window
    // over an undated one, and `.limit(1)` in SQL cannot express that. A site
    // has a handful of these, so there is nothing to save by narrowing here.
    db
      .from('bv_contract')
      .select('*, bv_contract_rate(*)')
      .eq('site_id', siteId)
      .eq('contract_type', 'network'),
    db
      .from('bv_contract')
      .select('*')
      .eq('site_id', siteId)
      .eq('contract_type', 'environmental'),
    db
      .from('site')
      .select('bv_extn')
      .eq('id', siteId)
      .single(),
  ]);

  // Rates live in bv_contract_rate (joined above). Usage rates and metering
  // charges are distinct columns on the same row (not a product_type split) —
  // a row may carry either or both, and different periods can carry each
  // independently, so each field-group is matched against billFrom on its own.
  let retail: RetailRateRow | null = null;
  let retailRateFallback = false;
  let metering: MeteringRateRow | null = null;
  if (retailRes.data) {
    const allDbRates = ((retailRes.data as unknown as { bv_contract_rate?: DbRateRow[] }).bv_contract_rate ?? []);

    // ── Energy rates ───────────────────────────────────────────────────────────
    // Banded feed-in counts as an energy rate too. A retailer that prices export
    // by time of use leaves feed_in_anytime NULL and sets the three band columns
    // instead, so a row carrying only those would otherwise be filtered out here
    // and fall through to the metering branch below.
    const hasEnergyRates = (r: DbRateRow) =>
      r.peak != null || r.shoulder != null || r.off_peak != null || r.super_peak != null ||
      r.feed_in_anytime != null || r.feed_in_peak != null ||
      r.feed_in_shoulder != null || r.feed_in_off_peak != null;
    const retailDbRates = allDbRates.filter(hasEnergyRates);
    const rates: RetailRateRow[] = retailDbRates.map(r => ({
      period_start: r.period_start,
      period_end: r.period_end,
      peak_rate: r.peak,
      shoulder_rate: r.shoulder,
      off_peak_rate: r.off_peak,
      super_peak_rate: r.super_peak,
      supply_charge: r.supply,
      // Was hardcoded to 'per_day' — silently ignored whatever unit was actually
      // configured on the contract (bv_contract_rate.supply_unit), so a monthly
      // or annual supply charge was always billed as if it were daily.
      supply_charge_unit: r.supply_unit ?? 'per_meter_day',
      feed_in_rate: r.feed_in_anytime,
      feed_in_peak_rate: r.feed_in_peak,
      feed_in_shoulder_rate: r.feed_in_shoulder,
      feed_in_off_peak_rate: r.feed_in_off_peak,
    }));
    const matching = rates.find(r =>
      (!r.period_start || r.period_start <= billFrom) &&
      (!r.period_end || r.period_end >= billFrom),
    );
    if (!matching && rates.length > 0) {
      retail = rates[0];
      retailRateFallback = true;
    } else {
      retail = matching ?? null;
    }

    // ── Metering charges ─────────────────────────────────────────────────────
    // `supply` is the retail supply/service charge and is already billed below
    // as the `Supply (...)` line. Only treat it as meter rental on rows that
    // carry no energy rates — i.e. metering-only rate rows. Previously any row
    // with both energy rates and a supply charge was picked up here too, so the
    // same `supply` value was billed twice: once as "Supply Charge" and again as
    // "Meter Rental", inflating the shadow total. Both lines now sit in the
    // metering section, so the duplicate would land in one section rather than
    // being split across two — still a double-bill, so this filter stays.
    // `hasEnergyRates` is the one defined above, which now also counts banded
    // feed-in; it used to be redeclared here with a narrower body.
    const meteringDbRates = allDbRates.filter(r =>
      r.metering != null || r.supplementary_metering != null || r.supplementary_services != null
      || (r.supply != null && !hasEnergyRates(r)),
    );
    const meteringMatch = meteringDbRates.find(r =>
      (!r.period_start || r.period_start <= billFrom) &&
      (!r.period_end || r.period_end >= billFrom),
    ) ?? (meteringDbRates.length > 0 ? meteringDbRates[0] : null);
    if (meteringMatch) {
      metering = {
        period_start: meteringMatch.period_start,
        period_end: meteringMatch.period_end,
        metering_annual: meteringMatch.metering ?? undefined,
        metering_unit: meteringMatch.metering_unit ?? undefined,
        // Same reason as the filter above: a row that also carries energy rates
        // has already had its `supply` billed as the `Supply (...)` line.
        supply_per_day: hasEnergyRates(meteringMatch) ? undefined : (meteringMatch.supply ?? undefined),
        supp_metering: meteringMatch.supplementary_metering ?? undefined,
        supp_metering_unit: meteringMatch.supp_metering_unit ?? undefined,
        supp_services: meteringMatch.supplementary_services ?? undefined,
        supp_services_unit: meteringMatch.supp_services_unit ?? undefined,
      };

      // A contract can hold its energy rates and its standing charges in
      // separate rate rows (Origin: one row per year of energy rates, one
      // metering row carrying Supply $40/servicePoint/month). The supply on the
      // metering row is then the retail supply charge, not meter rental: it
      // used to fall through to "Meter Rental" and be read as $/day, $1,240 a
      // month instead of $40 (NMI 41037502103, 41020102028, July 2026).
      if (!hasEnergyRates(meteringMatch) && meteringMatch.supply != null) {
        if (retail && retail.supply_charge == null) {
          retail = {
            ...retail,
            supply_charge: meteringMatch.supply,
            supply_charge_unit: meteringMatch.supply_unit ?? 'per_meter_day',
          };
          metering.supply_per_day = undefined;
        } else {
          // No energy row to carry it (or one that already has its own supply):
          // still a daily charge, so convert from the unit it was quoted in.
          metering.supply_per_day = supplyPerDay(meteringMatch.supply, meteringMatch.supply_unit);
        }
      }
    }
  }

  const bvExtn = (siteRes.data?.bv_extn ?? {}) as Record<string, unknown>;

  // Loss factors are resolved from the reference table by CODE, never read
  // from a number stored on the site. A stored value silently kept whatever
  // was resolved the first time, so loading a new financial year changed
  // nothing and old factors were applied to new bills.
  //
  // An override is the one exception: a number a person deliberately applied
  // (see LossFactorOffer, which offers the value the invoice itself states).
  // It is a separate field precisely so it cannot be confused with a cache.
  const mlfOverride = typeof bvExtn.mlf_override === 'number' ? bvExtn.mlf_override : null;
  const dlfOverride = typeof bvExtn.dlf_override === 'number' ? bvExtn.dlf_override : null;
  const mlfCode = String(bvExtn.mlf_code ?? bvExtn.tni ?? '').trim();
  const dlfCode = String(bvExtn.dlf_code ?? '').trim();

  const [mlfRow, dlfRow] = await Promise.all([
    mlfOverride === null && mlfCode
      ? db.from('transmission_loss_factor')
          .select('current_fy, current_mlf, prior_fy, prior_mlf')
          // Load before gen: a consumption site must not take the generation
          // MLF. Mirrors the ORDER BY in lossFactorRoutes' /mlf lookup.
          .or(`tni_code.ilike.${mlfCode},duid.ilike.${mlfCode}`)
          .order('factor_type', { ascending: false })
          .order('direction', { ascending: true, nullsFirst: true })
          .limit(1).maybeSingle()
      : Promise.resolve({ data: null }),
    dlfOverride === null && dlfCode
      ? db.from('distribution_loss_factor')
          .select('current_fy, current_dlf, prior_fy, prior_dlf')
          .or(`dlf_code.ilike.${dlfCode},nmi.ilike.${dlfCode}`)
          // A DLF code repeats across network levels and tariff classes — 63
          // codes match more than one row. Only one of them (GS93) carries
          // differing values, and by 0.00018, so which row wins barely matters;
          // ordering explicitly is so the SAME row wins every run rather than
          // the answer changing between recalculations.
          .order('dnsp', { ascending: true })
          .order('network_level', { ascending: true, nullsFirst: true })
          .limit(1).maybeSingle()
      : Promise.resolve({ data: null }),
  ]);

  const mlfPicked = mlfForPeriod(mlfRow.data as never, billFrom, billTo);
  const dlfPicked = dlfForPeriod(dlfRow.data as never, billFrom, billTo);

  const mlfValue = mlfOverride ?? mlfPicked.value;
  const dlfValue = dlfOverride ?? dlfPicked.value;
  const mlf_configured = typeof mlfValue === 'number';
  const dlf_configured = typeof dlfValue === 'number';

  return {
    retail,
    retailRateFallback,
    metering,
    distribution: networkFallbackRow(distRes.data as NetworkContractRow[] | null, billFrom),
    // Environmental configs used to be applied regardless of their period, so an
    // expired or not-yet-started certificate still produced a shadow charge that
    // no invoice matched — the site then read as an environmental variance even
    // though the certificate did not apply to the billing period at all.
    // The period lives on the bv_contract row (valid_from/valid_to), not in the
    // jsonb payload, and is filtered against billFrom the way distribution is.
    environmental: (envRes.data ?? [])
      .filter((row) => {
        const r = row as { valid_from?: string | null; valid_to?: string | null; is_current?: boolean | null };
        if (r.is_current === false) return false;
        return (!r.valid_from || r.valid_from <= billFrom)
          && (!r.valid_to || r.valid_to >= billFrom);
      })
      .map((row) => (row.data as EnvRow)),
    mlf: mlf_configured ? (mlfValue as number) : 1,
    dlf: dlf_configured ? (dlfValue as number) : 1,
    mlf_configured,
    dlf_configured,
    mlf_fy: mlfOverride !== null ? 'override' : mlfPicked.fy,
    dlf_fy: dlfOverride !== null ? 'override' : dlfPicked.fy,
  };
}

/**
 * How many physical meters the site had reporting during the billing period
 * (Job 60). Per-meter charges — `$/meter/year` metering — are priced per meter,
 * so this is the quantity their rate applies to.
 *
 * Counts distinct meter serials from bv_site_meter (migration 098), bounded to
 * meters whose reporting window OVERLAPS the period: a meter swapped out
 * mid-contract must not make the site look like it has two at any one moment.
 *
 * Returns null when nothing is registered — a site whose NEM12 has not been
 * synced since migration 098, or whose extract carries no meter serial. Null
 * means "unknown", and the caller keeps its historical single-meter behaviour
 * rather than silently billing a site for 0 meters.
 */
async function fetchMeterCount(
  db: SupabaseClient, siteId: string, from: string, to: string,
): Promise<number | null> {
  const { data, error } = await db
    .from('bv_site_meter')
    .select('meter_serial')
    .eq('site_id', siteId)
    .lte('first_seen_date', to)
    .gte('last_seen_date', from);

  if (error || !data) return null;
  if (data.length === 0) return null;
  return new Set(data.map((r) => (r as { meter_serial: string }).meter_serial)).size;
}

const INTERVAL_PAGE = 1000;

/**
 * Meters per day, read from the import channels in bv_nem_interval: each
 * distinct E-suffix (E1, E2, ...) with consumption on a day is one meter that
 * day — "however many versions of E there are, that's how many meters" (Josh,
 * 2026-10-14). Retailers bill per-meter charges as days x meters (NMI
 * 4204145291: E1 + E2 over 31 days = "62 days"), so this is that same figure.
 *
 * An all-zero channel is skipped, so the outgoing meter in a changeover —
 * which reports zeros while the new one takes over — does not count as a
 * second meter.
 *
 * Returns the average meters per day over the days that have data, so a
 * partial NEM12 extract still prices the full billing period. Null when the
 * period has no import interval data at all.
 */
async function fetchIntervalMeterCount(
  db: SupabaseClient, siteId: string, from: string, to: string,
): Promise<number | null> {
  const metersByDay = new Map<string, Set<string>>();
  for (let offset = 0; ; offset += INTERVAL_PAGE) {
    const { data, error } = await db
      .from('bv_nem_interval')
      .select('nmi, nmi_suffix, interval_date, values')
      .eq('site_id', siteId)
      .gte('interval_date', from)
      .lte('interval_date', to)
      .ilike('nmi_suffix', 'E%')
      .order('interval_date')
      .range(offset, offset + INTERVAL_PAGE - 1);
    if (error || !data) return null;

    for (const row of data as { nmi: string; nmi_suffix: string; interval_date: string; values: number[] | null }[]) {
      const hasUsage = (row.values ?? []).some((v) => Number(v) !== 0);
      if (!metersByDay.has(row.interval_date)) metersByDay.set(row.interval_date, new Set());
      if (hasUsage) metersByDay.get(row.interval_date)!.add(`${row.nmi}|${row.nmi_suffix.toUpperCase()}`);
    }
    if (data.length < INTERVAL_PAGE) break;
  }

  const daily = [...metersByDay.values()].map((s) => s.size).filter((n) => n > 0);
  if (daily.length === 0) return null;
  const average = daily.reduce((a, b) => a + b, 0) / daily.length;
  return Math.round(average * 100) / 100;
}

/** Interval channels first (always synced); the serial registry only when the
 *  period has no import interval data. */
async function resolveMeterCount(
  db: SupabaseClient, siteId: string, from: string, to: string,
): Promise<{ count: number | null; source: 'channels' | 'serials' | null }> {
  const fromChannels = await fetchIntervalMeterCount(db, siteId, from, to);
  if (fromChannels !== null) return { count: fromChannels, source: 'channels' };
  const fromSerials = await fetchMeterCount(db, siteId, from, to);
  return { count: fromSerials, source: fromSerials !== null ? 'serials' : null };
}

/** One priced network bucket, summed across the billing period. */
type NetworkCharge = {
  bucket_key: string;
  label: string;
  quantity: number;
  uom: string;
  rate: number | null;
  price_type: string | null;
  /** NULL when the price unit is not cost-convertible — never coerced to 0. */
  cost: number | null;
  /** Identify the bucket's row in Distribution Definitions. */
  tariff_code: string | null;
  distributor: string | null;
};

/** What Distribution Definitions currently says about one network bucket. */
type NetworkDefinition = {
  price: number | null;
  price_type: string | null;
};

/** Finds the definition for one priced bucket; undefined when none is configured. */
type DefinitionLookup = (
  distributor: string | null, tariffCode: string | null, bucketKey: string,
) => NetworkDefinition | undefined;

// The distributor is passed already resolved to its System Name: the usage row
// carries the site's spelling and the definition the config's, and the two
// only have to name the same network (migration 138).
const definitionKey = (
  distributor: string | null, tariffCode: string | null, bucketKey: string,
) => `${(distributor ?? '').toLowerCase()}|${(tariffCode ?? '').toLowerCase()}|${bucketKey.toLowerCase()}`;

/**
 * The CONFIGURED network prices — `bv_site_network`, which the UI presents as
 * the site's Distribution Definitions.
 *
 * Job 20: network rows are expected to state the unit the definition holds.
 * `bv_distribution_tariff_usage.price_type` is a COPY of it, stamped when the
 * day was priced (migration 071), so a definition corrected afterwards leaves
 * the comparison quoting the unit that was current at pricing time — with
 * nothing on screen saying the two now disagree.
 *
 * Read as a display source only. The stored `cost` was computed against the
 * price that was in force, and re-pricing it here would put a figure on screen
 * that no stored row supports.
 */
async function fetchNetworkDefinitions(
  db: SupabaseClient, charges: NetworkCharge[],
): Promise<DefinitionLookup> {
  const out = new Map<string, NetworkDefinition>();
  const none: DefinitionLookup = () => undefined;
  const codes = [...new Set(charges.map((c) => c.tariff_code).filter((c): c is string => !!c))];
  if (codes.length === 0) return none;

  const [{ data, error }, aliases] = await Promise.all([
    db.from('bv_site_network')
      .select('bucket_key, tariff_code, distributor, price, price_type')
      .in('tariff_code', codes),
    loadNameAliases(db, 'distributor'),
  ]);
  if (error || !data) return none;

  const dist = (d: string | null) => (d == null ? null : canonicalName(aliases, d));
  for (const r of data as Record<string, unknown>[]) {
    if (!r.bucket_key) continue;
    out.set(
      definitionKey(
        dist(r.distributor == null ? null : String(r.distributor)),
        r.tariff_code == null ? null : String(r.tariff_code),
        String(r.bucket_key),
      ),
      {
        price: r.price == null ? null : Number(r.price),
        price_type: r.price_type == null ? null : String(r.price_type),
      },
    );
  }
  return (distributor, tariffCode, bucketKey) =>
    out.get(definitionKey(dist(distributor), tariffCode, bucketKey));
}

/**
 * Network charges as the NEM Analysis pipeline actually computed them.
 *
 * The distributor's rates already live in bv_site_network and are applied per
 * day into bv_distribution_tariff_usage by bv_process_distribution_usage,
 * honouring the tariff's real window (weekday set, hours, months, holidays).
 * Reading them here is what makes the expected invoice agree with the
 * distributor's — the bv_contract `distribution` payload is a second, hand-kept
 * copy of the same rates that only ever held peak/shoulder/off-peak/supply and
 * drifts the moment a network price changes.
 *
 * Verified on Ausgrid EA225 (NMI 41035485976, July 2026): 334.272 kWh peak,
 * 1916.524 kWh off-peak, 31 supply days = $313.75, matching the retailer
 * invoice line for line.
 *
 * Costs are summed rather than re-derived from the period total: the stored
 * per-day `cost` is full precision (not pre-rounded), so the sum is exact, and
 * a treatment bucket's quantity is a maximum rather than a total — re-pricing
 * sum(quantity) would be wrong for it.
 *
 * TWO PASSES, and both are load-bearing. `bv_process_distribution_usage` splits
 * its output by treatment kind: a bucket whose treatment is `interval` (every
 * demand bucket — MAX_INTERVAL_LOOKBACK, MAX_INTERVAL_BILLING_MONTH) writes ONLY
 * to bv_distribution_treatment_interval and never produces a priced row in
 * bv_distribution_tariff_usage. Reading the day table alone therefore returns
 * usage and supply and silently drops the entire demand component — which on an
 * Ausgrid capacity tariff is the single largest charge on the bill. Pass 2
 * reduces those buckets through bv_reduce_bucket and prices them, mirroring
 * `buildBillLines` in server/features/billValidation/services/billRunService.ts,
 * which has always done both and carries the same warning.
 */

async function fetchNetworkCharges(
  db: SupabaseClient, siteId: string, from: string, to: string,
): Promise<NetworkCharge[]> {
  // ── pass 1: summed day rows ───────────────────────────────────────────────
  const { data, error } = await db
    .from('bv_distribution_tariff_usage')
    .select('bucket_key, bucket_label, quantity, uom, rate, price_type, cost, treatment_id, tariff_code, distributor')
    .eq('site_id', siteId)
    .gte('usage_date', from)
    .lte('usage_date', to);
  // Never fail the whole calculation over this — fall back to the contract path.
  if (error) return [];

  const byBucket = new Map<string, NetworkCharge>();
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    // A treatment bucket is reduced in pass 2, never summed. Summing a maximum
    // across 30 days would produce a number 30x too large.
    if (r.treatment_id) continue;
    const key = String(r.bucket_key);
    const prev = byBucket.get(key);
    const cost = r.cost == null ? null : Number(r.cost);
    byBucket.set(key, {
      bucket_key: key,
      label: String(r.bucket_label ?? key),
      quantity: (prev?.quantity ?? 0) + Number(r.quantity ?? 0),
      uom: String(r.uom ?? ''),
      rate: r.rate == null ? null : Number(r.rate),
      price_type: r.price_type == null ? null : String(r.price_type),
      // One unpriceable day makes the bucket unpriceable. A partial sum reads
      // as a complete charge and silently under-bills.
      cost: prev?.cost === null || cost === null ? null : (prev?.cost ?? 0) + cost,
      tariff_code: r.tariff_code == null ? null : String(r.tariff_code),
      distributor: r.distributor == null ? null : String(r.distributor),
    });
  }

  // ── pass 2: treatment (demand) buckets ────────────────────────────────────
  const { data: site } = await db
    .from('site').select('org_id, bv_extn').eq('id', siteId).single();
  const extn = (site?.bv_extn ?? {}) as Record<string, unknown>;
  const distributor = extn.network_distributor as string | undefined;
  const tariffCode = extn.network_tariff_code as string | undefined;
  // No tariff means nothing to reduce; the summed rows still stand.
  if (!site || !distributor || !tariffCode) return [...byBucket.values()];

  // Scoped to this site's org, distributor and tariff. Unscoped, this pulls
  // every treatment bucket in the catalogue and asks bv_reduce_bucket to price
  // a foreign tariff's bucket against this site, which raises and kills the
  // whole bill — the same trap billRunService documents. The database matches
  // the distributor by its approved System Name (migration 139), so a renamed
  // spelling on either side does not silently drop the demand charges.
  const { data: defs } = await db
    .rpc('bv_site_network_for_site', { p_site_id: siteId })
    .not('treatment_id', 'is', null)
    .select('bucket_key, name, price, price_type, treatment_params');

  const daysBilled = Math.round(
    (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000) + 1;

  for (const d of (defs ?? []) as Record<string, unknown>[]) {
    const { data: r, error: rErr } = await db.rpc('bv_reduce_bucket', {
      p_site_id: siteId,
      p_bucket_key: d.bucket_key as string,
      p_from: from,
      p_to: to,
      p_kind: '',
      p_params: d.treatment_params ?? {},
    });
    // A bucket that cannot be reduced is left out rather than billed at zero:
    // absent reads as "not calculated", zero reads as "nothing owed".
    // A site-fixed value (AusNet capacity) reads no intervals; see keepReducedBucket.
    if (rErr || !keepReducedBucket(r as Record<string, unknown>, d.treatment_params)) continue;
    const rec = r as Record<string, unknown>;
    const quantity = Number(rec.quantity);
    const rate = d.price == null ? null : Number(d.price);
    const priceType = d.price_type == null ? null : String(d.price_type);
    byBucket.set(String(d.bucket_key), {
      bucket_key: String(d.bucket_key),
      label: String(d.name ?? d.bucket_key),
      quantity,
      uom: String(rec.uom ?? ''),
      rate,
      price_type: priceType,
      cost: costOf({ quantity, rate, price_type: priceType, daysBilled }),
      // A reduced bucket has no day row to carry these, but it still has to be
      // findable in Distribution Definitions like any other charge. They come
      // from the site, which is what scoped the definition query above.
      tariff_code: tariffCode,
      distributor,
    });
  }

  return [...byBucket.values()];
}

function line(
  section: ShadowBillSection,
  description: string,
  volume: number,
  volumeUnit: string,
  rate: number,
  dlf: number,
  mlf: number,
): ShadowBillLine {
  const adj_rate = rate * dlf * mlf;
  // A consumption charge counts nothing — it is priced per unit consumed, so the
  // figure belongs under Usage and QTY is left empty rather than repeating it.
  // That repetition is what Erin reported as "some information is repeated".
  return {
    section,
    description,
    usage: volume,
    usage_unit: volumeUnit,
    rate,
    // Job 20: `line` is the cents-per-unit form — the amount divides by 100 — so
    // the unit follows the volume: ¢/kWh for usage, ¢/kVA for a demand quantity.
    rate_unit: `¢/${volumeUnit}`,
    dlf,
    mlf,
    adj_rate,
    amount: parseFloat((volume * adj_rate / 100).toFixed(2)), // rate in c/kWh → divide by 100
  };
}

/**
 * A feed-in credit. Same DLF x MLF adjustment as `line()` — the retailer applies
 * losses to export exactly as it does to consumption. AGL invoice 429b3926:
 * buy-back 2.4707 x 1.0398 x 1.0102 = 2.5952, and -(1498.32 x 2.5952 / 100) =
 * -38.88, which is the printed amount to the cent.
 *
 * Differs from `line()` in the sign convention, which follows how the retailer
 * PRINTS it: rate positive, amount negative. The magnitude is taken with abs()
 * rather than trusting the stored sign — `bv_contract_rate.feed_in_*` is stored
 * negative (all 182 prod rows with feed_in_anytime set are), and the previous
 * code multiplied that negative rate by a leading minus, turning every credit
 * into a charge. abs() makes the direction of this line a property of the line,
 * not of the data, so a rate stored either way yields a credit.
 */
export function feedInLine(
  description: string, volume: number, rate: number, dlf: number, mlf: number,
): ShadowBillLine {
  const magnitude = Math.abs(rate);
  const adj_rate = magnitude * dlf * mlf;
  return {
    section: 'retail',
    description,
    usage: volume,
    usage_unit: 'kWh',
    rate: magnitude,
    rate_unit: '¢/kWh',
    dlf,
    mlf,
    adj_rate,
    amount: parseFloat((-volume * adj_rate / 100).toFixed(2)),
  };
}

function dayLine(
  section: ShadowBillSection,
  description: string,
  days: number,
  ratePerDay: number,
): ShadowBillLine {
  return {
    section,
    description,
    // A daily charge runs over days — that is Usage, not a count of things. It
    // is charged per service point, and a site is one, so QTY says so rather
    // than sitting empty next to a $/servicePoint/day rate.
    volume: 1,
    volume_unit: 'servicePoint',
    usage: days,
    usage_unit: 'days',
    rate: ratePerDay,
    // Job 20: the contracted rate has already been converted to a daily figure
    // here, so the unit is $/day even when the charge is named "($/.../month)".
    // Stating it stops the name and the number contradicting each other.
    rate_unit: '$/day',
    amount: parseFloat((days * ratePerDay).toFixed(2)),
  };
}

export async function calculateShadowBill(db: SupabaseClient, bill: BvBill): Promise<ShadowBillResult> {
  const [contracts, networkCharges, meters] = await Promise.all([
    fetchContracts(db, bill.site_id, bill.bill_from, bill.bill_to),
    fetchNetworkCharges(db, bill.site_id, bill.bill_from, bill.bill_to),
    resolveMeterCount(db, bill.site_id, bill.bill_from, bill.bill_to),
  ]);
  const meterCount = meters.count;
  const warnings: string[] = [];

  // Retail kWh — classified using the retailer's TOU windows
  const peak = bill.peak_kwh ?? 0;
  const shoulder = bill.shoulder_kwh ?? 0;
  const offPeak = bill.off_peak_kwh ?? 0;
  // Migration 144. Zero on bills applied before it, whose Super Peak kWh (if
  // any) were counted as off-peak.
  const superPeak = bill.super_peak_kwh ?? 0;
  const totalKwh = bill.total_kwh ?? (superPeak + peak + shoulder + offPeak);
  const feedIn = bill.feed_in_kwh ?? 0;
  // Solar export banded by the retailer's windows (migration 127). NULL on bills
  // applied before it, and on sites whose export is not in NEM12 channel B — the
  // unbanded `feedIn` above still covers those.
  const feedInPeak = bill.feed_in_peak_kwh ?? 0;
  const feedInShoulder = bill.feed_in_shoulder_kwh ?? 0;
  const feedInOffPeak = bill.feed_in_off_peak_kwh ?? 0;
  const demand = bill.peak_demand_kva ?? 0;
  const days = bill.billing_days ?? 30;
  const { mlf, dlf } = contracts;

  // Network kWh — classified using the distributor's TOU windows (different to retail).
  // Falls back to retail kWh if not yet auto-populated (backwards compatibility).
  const netPeak     = bill.network_peak_kwh     ?? peak;
  const netShoulder = bill.network_shoulder_kwh ?? shoulder;
  const netOffPeak  = bill.network_off_peak_kwh ?? offPeak;
  const netTouPopulated = bill.network_peak_kwh != null;

  if (!contracts.mlf_configured) warnings.push('MLF not configured on site — using 1.0000 (retail charges may be inaccurate)');
  if (!contracts.dlf_configured) warnings.push('DLF not configured on site — using 1.0000 (retail charges may be inaccurate)');
  // Factors are resolved per bill now, so they cannot be stale. What CAN
  // happen is a code that resolves to nothing — the reference table has not
  // been loaded for that year, or the code is wrong — which silently costs the
  // bill at 1.0. The two warnings above already cover that case; this one adds
  // the financial year actually applied, including a blend across 1 July.
  if (contracts.mlf_configured && contracts.mlf_fy?.includes('+')) {
    warnings.push(`MLF blended across the 1 July boundary (${contracts.mlf_fy}), weighted by days in each year`);
  }
  if (contracts.dlf_configured && contracts.dlf_fy?.includes('+')) {
    warnings.push(`DLF blended across the 1 July boundary (${contracts.dlf_fy}), weighted by days in each year`);
  }
  if (contracts.retailRateFallback) warnings.push(`No retail rate period covers ${bill.bill_from} — using oldest available rate (results may be inaccurate)`);
  // Only a real caveat when the contract path is actually in use. With computed
  // network charges the distribution section is priced from the distributor's
  // own windows and never touches these kWh, so the warning would be noise.
  if (!netTouPopulated && networkCharges.length === 0) {
    warnings.push('Network kWh not separately populated — using retail TOU kWh for distribution charges (may be inaccurate). Run "Use NEM Analysis" to fix.');
  }

  const lines: ShadowBillLine[] = [];

  // ── RETAIL ──────────────────────────────────────────────────────────────────
  if (contracts.retail) {
    const r = contracts.retail;
    if (!r.peak_rate && !r.shoulder_rate && !r.off_peak_rate && !r.super_peak_rate) {
      warnings.push('No retail rates found for this period');
    }
    // A Super Peak window on a contract with no Super Peak rate stays off-peak,
    // as bv_process_retail_tou prices it (migration 144) and as it was before
    // the band existed. Nectr NSW's peak-only plans do not bill those hours as Peak.
    //
    // A Super Peak plan with no Peak or Shoulder rate is a two-rate plan: the
    // retailer bills everything outside Super Peak as Off-Peak (Nectr NSW,
    // NMI 43103214635, June 2026: Off-Peak 4,344.389 kWh = all but Super Peak).
    // The retailer's Peak/Shoulder windows still split those kWh, and with no
    // rate for them they were dropped.
    const twoRate = !!r.super_peak_rate && !r.peak_rate && !r.shoulder_rate;
    const retailOffPeak = (r.super_peak_rate ? offPeak : offPeak + superPeak) + (twoRate ? peak + shoulder : 0);
    if (superPeak > 0 && r.super_peak_rate) lines.push(line('retail', RETAIL_ENERGY_NAMES.superPeak, superPeak, 'kWh', r.super_peak_rate, dlf, mlf));
    if (peak > 0 && r.peak_rate) lines.push(line('retail', RETAIL_ENERGY_NAMES.peak, peak, 'kWh', r.peak_rate, dlf, mlf));
    if (shoulder > 0 && r.shoulder_rate) lines.push(line('retail', RETAIL_ENERGY_NAMES.shoulder, shoulder, 'kWh', r.shoulder_rate, dlf, mlf));
    if (retailOffPeak > 0 && r.off_peak_rate) lines.push(line('retail', RETAIL_ENERGY_NAMES.offPeak, retailOffPeak, 'kWh', r.off_peak_rate, dlf, mlf));
    if (r.supply_charge) {
      // Real stored values are BvRateChargeUnit: per_{meter,servicepoint}_{day,month,year}.
      // The meter/servicepoint distinction isn't modeled here (no per-meter multiplier
      // exists in this calc) — only the day/month/year time unit affects the amount.
      // Contract's own unit only exists here — everything downstream (comparison table,
      // PDF report) reads off `description` alone, so the unit is folded into the label
      // rather than silently vanishing after normalization.
      const isYearly = r.supply_charge_unit?.endsWith('_year');
      const isMonthly = r.supply_charge_unit?.endsWith('_month');
      const dailyRate = isYearly ? r.supply_charge / 365
        : isMonthly ? r.supply_charge / 30.4368
        : r.supply_charge;
      // Supply belongs in metering, always. It is read off the retail contract
      // (bv_contract_rate.supply) but it is a standing charge for the connection,
      // not energy — which is how `chargeNames.RATE_ROWS` has always catalogued
      // it ("Metering Charges"). It used to be emitted under retail, which meant
      // it was billed correctly but grouped wrongly, and correcting that needed a
      // per-retailer section override per retailer (job 59). Emitting it here
      // instead makes the grouping right everywhere at once — Expected Invoice
      // tab, comparison table and the PDF report all read the section off the
      // line. The actual-invoice side is moved to match by
      // `reclassifySupplyLines` in invoiceParserService, so the two sides still
      // pair in the comparison.
      lines.push(dayLine('metering', supplyChargeName(r.supply_charge_unit), days, dailyRate));
    }
    // Feed-in. Banded first: a retailer that prices export by time of use bills
    // one line per window, and collapsing them to a single anytime line would
    // price the whole export at one rate — wrong whenever the bands differ (AGL
    // peak/shoulder 2.4707 vs off-peak 2.4657).
    //
    // Per band, the rate falls back to the anytime rate, for a contract that
    // prices some bands and leaves the rest to its anytime rate.
    //
    // A contract with ONLY an anytime rate bills export as one line, whatever
    // the windows split it into: the retailer prints one "Feed-in Tariff" line,
    // which the charge map pairs with Solar Feed-in Anytime. Three banded lines
    // at the same rate never paired with it (176 prod contracts are anytime-only;
    // July 2026 bills showed only Peak/Off-Peak feed-in lines, no Anytime).
    const feedInBands: Array<[string, number, number | undefined]> = [
      [RETAIL_ENERGY_NAMES.feedInPeak, feedInPeak, r.feed_in_peak_rate ?? r.feed_in_rate],
      [RETAIL_ENERGY_NAMES.feedInShoulder, feedInShoulder, r.feed_in_shoulder_rate ?? r.feed_in_rate],
      [RETAIL_ENERGY_NAMES.feedInOffPeak, feedInOffPeak, r.feed_in_off_peak_rate ?? r.feed_in_rate],
    ];
    const bandedFeedIn = feedInBands.filter(([, kwh]) => kwh > 0);
    const contractBandsFeedIn =
      r.feed_in_peak_rate != null || r.feed_in_shoulder_rate != null || r.feed_in_off_peak_rate != null;

    if (bandedFeedIn.length > 0 && !contractBandsFeedIn && r.feed_in_rate) {
      const bandedKwh = bandedFeedIn.reduce((s, [, kwh]) => s + kwh, 0);
      lines.push(feedInLine(RETAIL_ENERGY_NAMES.feedInAnytime, bandedKwh, r.feed_in_rate, dlf, mlf));
    } else if (bandedFeedIn.length > 0) {
      for (const [name, kwh, rate] of bandedFeedIn) {
        if (rate) lines.push(feedInLine(name, kwh, rate, dlf, mlf));
      }
      // Priced at nothing despite having the volumes — say so rather than
      // leaving the export silently absent from the total. Feed-in rates are
      // missing on most solar sites (134 of 159 in prod at the time of writing).
      if (!bandedFeedIn.some(([, , rate]) => rate)) {
        warnings.push('Solar export banded but not priced — no feed-in rates on the retail contract for this period');
      }
    } else if (feedIn > 0) {
      // Unbanded: one anytime line, the historical behaviour.
      if (r.feed_in_rate) {
        lines.push(feedInLine(RETAIL_ENERGY_NAMES.feedInAnytime, feedIn, r.feed_in_rate, dlf, mlf));
      } else {
        warnings.push('Solar export present but not priced — no feed-in rate on the retail contract for this period');
      }
    }
  } else {
    warnings.push('No current retail contract found');
  }

  // ── DISTRIBUTION ────────────────────────────────────────────────────────────
  // Uses network TOU kWh (different peak windows to retail — e.g. Energex 6800
  // peak = 5pm–8pm, vs Shell QLD peak = 7am–11pm). Falls back to retail kWh
  // with a warning if network kWh has not been auto-populated yet.
  // Looked up only when there are network charges to describe — a site billed
  // off the contract payload has no buckets to match a definition against.
  const definitionOf: DefinitionLookup = networkCharges.length > 0
    ? await fetchNetworkDefinitions(db, networkCharges)
    : () => undefined;

  if (networkCharges.length > 0) {
    // Preferred source: what the NEM Analysis pipeline actually priced, using
    // the distributor's real tariff windows. Falls through to the contract
    // payload below only when no usage has been computed for this period.
    for (const b of networkCharges) {
      if (b.cost === null) {
        warnings.push(
          `Network charge "${b.label}" is priced in ${b.price_type ?? 'an unrecognised unit'}`
          + ' — quantity shown, cost not calculated',
        );
        continue;
      }
      // Job 20: state the unit Distribution Definitions holds, not the copy of it
      // stamped onto the usage row when the day was priced. Where no definition
      // is found the stamped values stand — a missing definition is not a reason
      // to show less than we already know.
      const def = definitionOf(b.distributor, b.tariff_code, b.bucket_key);
      const rateUnit = def?.price_type ?? b.price_type ?? null;
      const volumeUnit = quantityUnitForPriceType(def?.price_type) ?? b.uom;

      // The definition has moved since this was priced. The stored cost is still
      // the honest record of what was charged, so it is left alone and the
      // divergence is reported instead of being silently papered over.
      if (def && def.price != null && b.rate != null && Math.abs(def.price - b.rate) > 1e-6) {
        warnings.push(
          `"${b.label}" was priced at ${b.rate} but Distribution Definitions now says ${def.price}`
          + ' — re-run NEM analysis to reprice.',
        );
      }

      // The pipeline reports ONE quantity per bucket, and which column it belongs
      // in depends on what the bucket charges for:
      //   kW / kVA  → a demand QTY, billed across the period, so days go beside it
      //   days      → a daily charge; the days ARE the usage, nothing is counted
      //   kWh       → consumption, which is usage
      // Routing every bucket to QTY put kWh under a column that means "how many
      // things", which is what made the network rows unreadable.
      const qtyUnits = /^(kw|kva)$/i;
      const isDemand = qtyUnits.test(volumeUnit.trim());
      const isDaily = /^days?$/i.test(volumeUnit.trim());
      const quantity = parseFloat(b.quantity.toFixed(3));

      lines.push({
        section: 'distribution',
        description: b.label,
        ...(isDemand
          ? { volume: quantity, volume_unit: volumeUnit, usage: days, usage_unit: 'days' }
          : isDaily
            ? { usage: quantity, usage_unit: 'days' }
            : { usage: quantity, usage_unit: volumeUnit }),
        ...(b.rate == null ? {} : { rate: b.rate }),
        rate_unit: rateUnit,
        amount: parseFloat(b.cost.toFixed(2)),
      });
    }
  } else if (contracts.distribution) {
    const d = contracts.distribution;
    if (d.supply_charge_per_day) lines.push(dayLine('distribution', 'Network Standing Charge', days, d.supply_charge_per_day));
    if (netPeak > 0 && d.peak_charge) lines.push(line('distribution', 'Peak Network', netPeak, 'kWh', d.peak_charge, 1, 1));
    if (netShoulder > 0 && d.shoulder_charge) lines.push(line('distribution', 'Shoulder Network', netShoulder, 'kWh', d.shoulder_charge, 1, 1));
    if (netOffPeak > 0 && d.off_peak_charge) lines.push(line('distribution', 'Off-Peak Network', netOffPeak, 'kWh', d.off_peak_charge, 1, 1));
    // A flat network tariff quotes Anytime and nothing else. Charged over the
    // whole network volume, and only when no TOU rate is set — a tariff holding
    // both would otherwise bill the same kWh twice.
    if (!d.peak_charge && !d.shoulder_charge && !d.off_peak_charge && d.anytime_charge) {
      const netTotal = netPeak + netShoulder + netOffPeak;
      if (netTotal > 0) {
        lines.push(line('distribution', 'Anytime Network', netTotal, 'kWh', d.anytime_charge, 1, 1));
      }
    }
    if (d.has_demand_contract && demand > 0) {
      if (d.peak_demand_rate) {
        const demandAmt = parseFloat((demand * d.peak_demand_rate * days / 365).toFixed(2));
        // Demand is the QTY (kVA charged), prorated over the period — so the days
        // belong beside it under Usage, the same shape as metering.
        lines.push({ section: 'distribution', description: 'Peak Demand Charge', volume: demand, volume_unit: 'kVA', usage: days, usage_unit: 'days', rate: d.peak_demand_rate, rate_unit: '$/kVA/year', amount: demandAmt });
      }
      if (d.anytime_demand_rate) {
        const demandAmt = parseFloat((demand * d.anytime_demand_rate * days / 365).toFixed(2));
        lines.push({ section: 'distribution', description: 'Anytime Demand Charge', volume: demand, volume_unit: 'kVA', usage: days, usage_unit: 'days', rate: d.anytime_demand_rate, rate_unit: '$/kVA/year', amount: demandAmt });
      }
      if (!d.peak_demand_rate && !d.anytime_demand_rate) {
        warnings.push('Demand contract active but no demand rates set — demand charges not calculated');
      }
    }
  } else {
    // Names what is actually missing. "No distribution config found" pointed at
    // a section that no longer exists, and did not say whether the problem was a
    // missing tariff code, a missing window or missing rates.
    warnings.push(
      'No network charges for this period — assign a network tariff code covering '
      + `${bill.bill_from} under Contracts, with a rate period that prices it, `
      + 'or run "Use NEM Analysis" so network usage is priced from the catalog.',
    );
  }

  // ── METERING ─────────────────────────────────────────────────────────────────
  // metering_annual = $/year annual fee (prorated to billing period)
  // supply_per_day  = $/day meter rental / communication charge
  if (contracts.metering) {
    const m = contracts.metering;
    let meterWarned = false;

    // One per-meter contract charge, prorated over the bill. Metering itself and
    // the contract's Supplementary Metering and Supplementary Services fields all
    // follow this rule: same units, same meter count, same proration.
    const perMeterLine = (name: string, amountPerUnit: number, unit: string | undefined) => {
      // The unit is now editable on the contract, so it has to be honoured. Reading
      // a $/day figure as annual — or the reverse — is a 365x error, which is the
      // same mistake that had the retail supply charge billed as daily meter rental.
      // An absent unit keeps the historical assumption of $/year.
      const isDaily = unit?.endsWith('_day');
      const isMonthly = unit?.endsWith('_month');
      const dailyRate = isDaily ? amountPerUnit
        : isMonthly ? amountPerUnit / 30.4368
        : amountPerUnit / 365;

      // Job 60: "Metering - QTY how many meters, usage amount of days billed".
      // BOTH, not one instead of the other — a $/meter/annum charge prorated
      // over a period needs the meter count AND the days to be checkable, which
      // is why the spec keeps QTY and Usage as separate columns for this row.
      //
      // A per_servicepoint_* unit counts service points, and a site is one
      // connection point, so that is 1. A per_meter_* unit counts meters, from
      // the NEM12 E channels with usage (resolveMeterCount).
      const holder = chargeHolderOf(unit, 'meter');
      const meterQty = holder === 'meter' ? (meterCount ?? 1) : 1;
      if (holder === 'meter' && !meterWarned) {
        meterWarned = true;
        if (meterCount === null) {
          // Assumed, not measured — say so rather than presenting a bare "1 meter"
          // as if the data supported it.
          warnings.push(
            'No NEM12 import channels or meter serials for this period — metering assumes 1 meter. '
            + 'Re-run the GCS sync to load interval data.',
          );
        } else if (meterQty !== 1) {
          // The charge multiplies, so the total moves. Multi-meter sites were
          // previously billed for exactly one meter here; that is a real change to
          // an expected total and must not happen silently.
          const basis = meters.source === 'channels'
            ? 'NEM12 E channels with usage, averaged per day'
            : 'NEM12 meter serials';
          warnings.push(
            `Metering charged across ${meterQty} meters for this period (${basis}).`,
          );
        }
      }

      lines.push({
        section: 'metering',
        description: name,
        volume: meterQty,
        volume_unit: meterQty === 1 ? holder === 'meter' ? 'meter' : 'servicePoint'
          : holder === 'meter' ? 'meters' : 'servicePoints',
        // The days the charge applies to — the second half of Job 60, and what
        // makes an annual rate against a 31-day period verifiable.
        usage: days,
        usage_unit: 'days',
        rate: amountPerUnit,
        // Unlike dayLine, this keeps the contracted figure, so the unit is the
        // contracted one — the $/meter/year that Erin needs to see against a
        // retailer's $/meter/day.
        rate_unit: chargeRateUnit(unit, 'meter', 'year'),
        amount: parseFloat((dailyRate * days * meterQty).toFixed(2)),
      });
    };

    if (m.metering_annual) perMeterLine(meteringChargeName(m.metering_unit), m.metering_annual, m.metering_unit);
    // Stored on the contract and shown on its screen, but never priced until
    // 2026-10-08: 706 prod contracts carry Supplementary Metering and 539
    // Supplementary Services, which retailers bill as "Supplementary Metering
    // Charge" / "Value Added Service Charge" (Origin, NMI 41022159180).
    if (m.supp_metering) {
      perMeterLine(perMeterChargeName('Supplementary Metering', m.supp_metering_unit), m.supp_metering, m.supp_metering_unit);
    }
    if (m.supp_services) {
      perMeterLine(perMeterChargeName('Supplementary Services', m.supp_services_unit), m.supp_services, m.supp_services_unit);
    }
    if (m.supply_per_day) {
      // Kept as 'Meter Rental' (not aligned to a Master Data name): the sheet's
      // metering tab has no unambiguous equivalent for a second daily metering
      // line distinct from the annual `Metering (...)` charge above. Pending
      // confirmation of which Master Data name, if any, this should map to.
      lines.push(dayLine('metering', 'Meter Rental', days, m.supply_per_day));
    }
  }

  // ── ENVIRONMENTAL ────────────────────────────────────────────────────────────
  // DLF applies (cert volume is measured at the grid connection point, adjusted for distribution losses)
  // MLF does NOT apply (environmental certs are not subject to transmission loss adjustment)
  for (const env of contracts.environmental) {
    if (env.rate && totalKwh > 0) {
      lines.push(line('environmental', env.certificate_type, totalKwh, 'kWh', env.rate, dlf, 1));
    }
  }

  // ── SECTION TOTALS ───────────────────────────────────────────────────────────
  const sectionTotals: Record<ShadowBillSection, number> = {
    retail: 0, distribution: 0, metering: 0, environmental: 0, other: 0, gst: 0,
  };
  for (const l of lines) {
    sectionTotals[l.section] = parseFloat(((sectionTotals[l.section] ?? 0) + l.amount).toFixed(2));
  }

  // ── GST (10% on all charge categories — environmental certs are GST-inclusive in AU retail bills) ──
  const taxableBase = sectionTotals.retail + sectionTotals.distribution + sectionTotals.metering + sectionTotals.environmental;
  const gst = parseFloat((taxableBase * 0.1).toFixed(2));
  sectionTotals.gst = gst;
  lines.push({ section: 'gst', description: 'GST (10%)', amount: gst });

  const subtotal = parseFloat(taxableBase.toFixed(2));
  const total = parseFloat((subtotal + gst).toFixed(2));

  return {
    bill_from: bill.bill_from,
    bill_to: bill.bill_to,
    billing_days: days,
    total_kwh: totalKwh,
    lines,
    section_totals: sectionTotals,
    subtotal,
    gst,
    total,
    calculated_at: new Date().toISOString(),
    warnings,
  };
}
